import { useState, useEffect } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { GamePurchaseModal } from "@/components/game/GamePurchaseModal";
import { GameNFTMiniCard } from "@/components/game/GameNFTMiniCard";
import { supabase } from "@/integrations/supabase/client";
import { Navigate, Link } from "react-router-dom";
import { Flame, Crown, Sparkles, Clock, ChevronRight } from "lucide-react";
import cyberTrending from "@/assets/game/cyber-trending.png";
import cyberExpensive from "@/assets/game/cyber-expensive.png";
import cyberRecent from "@/assets/game/cyber-recent.png";
import cyberTopSellers from "@/assets/game/cyber-topsellers.png";
import gameBg from "@/assets/game-bg.png";

interface GameNFT {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  current_price: number;
  sales_count: number;
  bonding_curve_step: number;
  is_listed: boolean;
  created_at: string;
  creator_id: string;
  owner_id: string;
  creator_name?: string;
  owner_name?: string;
}

export default function GameMarketplace() {
  const { session, player, loading, signOut } = useGameAuth();
  const [nfts, setNfts] = useState<GameNFT[]>([]);
  const [trending24h, setTrending24h] = useState<Map<string, number>>(new Map());
  const [selectedNft, setSelectedNft] = useState<GameNFT | null>(null);
  const [loadingNfts, setLoadingNfts] = useState(true);

  useEffect(() => {
    fetchNfts();
  }, []);

  const fetchNfts = async () => {
    setLoadingNfts(true);
    // Recent (created_at desc) — capped at 1000 by PostgREST
    const { data: recentData } = await supabase
      .from("game_nfts")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);
    // Most Expensive — separate query so high-price old NFTs aren't cut off
    const { data: expensiveData } = await supabase
      .from("game_nfts")
      .select("*")
      .order("current_price", { ascending: false })
      .limit(20);

    // Merge unique by id
    const map = new Map<string, any>();
    [...(recentData || []), ...(expensiveData || [])].forEach((n) => map.set(n.id, n));
    const nftData = Array.from(map.values());

    // Trending = sales in last 24h
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: txData } = await supabase
      .from("game_transactions")
      .select("nft_id")
      .gte("created_at", since);
    const counts = new Map<string, number>();
    (txData || []).forEach((t: any) => {
      counts.set(t.nft_id, (counts.get(t.nft_id) || 0) + 1);
    });
    setTrending24h(counts);

    if (nftData.length > 0) {
      const playerIds = [...new Set([
        ...nftData.map((n: any) => n.creator_id),
        ...nftData.map((n: any) => n.owner_id),
      ])];

      const { data: players } = await supabase
        .from("game_players")
        .select("id, display_name")
        .in("id", playerIds);

      const playerMap = new Map(players?.map((p: any) => [p.id, p.display_name]) || []);

      setNfts(nftData.map((n: any) => ({
        ...n,
        creator_name: playerMap.get(n.creator_id) || "Unknown",
        owner_name: playerMap.get(n.owner_id) || "Unknown",
      })));
    } else {
      setNfts([]);
    }
    setLoadingNfts(false);
  };

  if (loading) return null;
  if (!session || !player) return <Navigate to="/game" replace />;

  // Derive 3 sections (max 10 NFTs each, auto-updating from live data)
  const recent = [...nfts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 10);
  const trending = [...nfts]
    .map((n) => ({ n, c: trending24h.get(n.id) || 0 }))
    .filter((x) => x.c > 0)
    .sort((a, b) => b.c - a.c)
    .map((x) => x.n)
    .slice(0, 10);
  const mostExpensive = [...nfts].sort((a, b) => b.current_price - a.current_price).slice(0, 10);

  const sections = [
    { title: "🆕 Recent", icon: Clock, color: "text-cyan-400", data: recent, path: "/game/recent", emptyText: "No NFTs in this category" },
    { title: "🔥 Trending (24h)", icon: Flame, color: "text-orange-400", data: trending, path: "/game/trending", emptyText: "No sales in the last 24 hours" },
    { title: "💰 Most Expensive", icon: Crown, color: "text-yellow-400", data: mostExpensive, path: "/game/most-expensive", emptyText: "No NFTs in this category" },
  ];

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div
        className="relative max-w-6xl mx-auto px-3 pt-8 pb-4 md:pt-10 space-y-4 bg-center bg-cover bg-no-repeat bg-fixed"
        style={{ backgroundImage: `url(${gameBg})` }}
      >
        <div className="absolute inset-0 bg-black/75 backdrop-blur-[2px] -z-0" aria-hidden="true" />
        <div className="relative z-10 space-y-4">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-game-pink" style={{ filter: 'drop-shadow(0 0 6px rgba(236, 72, 153, 0.6))' }} />
          <h1 className="text-xl font-extrabold text-game-gradient">Marketplace</h1>
        </div>

        {loadingNfts ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-500 border-t-transparent" />
          </div>
        ) : nfts.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <div className="w-16 h-16 rounded-full bg-black/60 flex items-center justify-center mx-auto border border-purple-500/30">
              <Sparkles className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-sm font-semibold text-gray-500">No NFTs for sale yet</p>
            <Link to="/game/mint">
              <button className="font-bold btn-game-neon px-4 py-2 rounded-lg text-sm border-0">
                ⚡ Mint the first NFT!
              </button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {sections.map((section) => (
              <div
                key={section.path}
                className="rounded-xl p-3 border border-purple-500/25 bg-black/40 backdrop-blur-sm"
              >
                <div className="flex items-center justify-between mb-2">
                  <h2 className={`text-sm font-extrabold ${section.color} flex items-center gap-1.5`} style={{ textShadow: '0 0 10px currentColor' }}>
                    {section.title}
                  </h2>
                  <Link to={section.path} className="flex items-center gap-0.5 text-[11px] text-gray-300 hover:text-white transition-colors">
                    View All <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                {section.data.length === 0 ? (
                  <p className="text-xs text-gray-500 py-4 text-center">{section.emptyText}</p>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-none" style={{ scrollbarWidth: "none" }}>
                    {section.data.map((nft) => (
                      <GameNFTMiniCard
                        key={nft.id}
                        nft={nft}
                        playerId={player.id}
                        onBuy={() => setSelectedNft(nft)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      <GamePurchaseModal
        nft={selectedNft}
        playerId={player.id}
        balance={player.virtual_balance}
        open={!!selectedNft}
        onClose={() => setSelectedNft(null)}
        onSuccess={() => {
          fetchNfts();
          window.location.reload();
        }}
      />
    </GameLayout>
  );
}
